import { Delete, HourglassTop, Warning } from "@mui/icons-material";
import { Box, IconButton, LinearProgress } from "@mui/material";
import type { FC } from "react";
import CheckIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import formatSizeBytes from "helpers/formatSizeBytes";
import { FileUpload } from "./multipart-upload.types";
import { friendlyErrorMessage } from "./friendlyErrorMessage";
import theme from "theme";
import { cn } from "components/lib/utils";

interface Props {
  files: FileUpload[];
  onFileDelete?: (file: FileUpload) => void;
}

const FileUploadProgress: FC<Props> = ({ files, onFileDelete }) => {
  return (
    <Box
      sx={{
        my: 2,
        maxHeight: 320,
        overflow: "auto",
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.grey[100]}`,
        pointerEvents: "none",
      }}>
      {files.map((f) => {
        const friendlyErrorInfo = f.state === "error" && friendlyErrorMessage(f.error, f.clientError);
        return (
          <div
            className={cn("w-full flex items-center p-2", f.state === "inprogress" && "bg-gray-100")}
            key={`${f.file.name}-${f.batchId}`}>
            <div className="flex items-center overflow-auto grow gap-1">
              <p className="text-sm font-medium truncate" title={f.file.name}>
                {f.file.name}
              </p>
              <p className="text-sm font-medium">{formatSizeBytes(f.file.size)}</p>
            </div>
            {f.state === "inprogress" && (
              <div className="flex items-center w-96 gap-1 ">
                {f.progress === 100 ? (
                  <>
                    <LinearProgress variant="indeterminate" sx={{ flexGrow: 1 }} />
                    <p className="text-sm font-medium">Validating</p>
                  </>
                ) : (
                  <LinearProgress className="w-full" variant="determinate" value={f.progress} />
                )}
              </div>
            )}
            {f.state === "success" && (
              <div className="flex items-center">
                <div className="grow" />
                <div className="flex items-center gap-1">
                  <p className="text-sm font-medium">Success</p>
                  <CheckIcon className="text-green-500" />
                </div>
              </div>
            )}
            {f.state === "queued" && (
              <div className="flex items-center text-gray-400">
                <p className="text-sm font-medium">Queued</p>
                <HourglassTop fontSize="small" />
              </div>
            )}
            {f.state === "error" && (
              <div className="flex items-center gap-1 max-w-96">
                <p className="text-sm font-medium truncate">{friendlyErrorInfo[0]}</p>
                {friendlyErrorInfo[1] === "error" && <ErrorIcon className="text-red-500" />}
                {friendlyErrorInfo[1] === "warning" && <Warning className="text-yellow-500" />}
              </div>
            )}
            {f.state === "idle" && onFileDelete && !f.error && (
              <IconButton size="small" onClick={() => onFileDelete(f)} className="pointer-events-auto">
                <Delete />
              </IconButton>
            )}
          </div>
        );
      })}
    </Box>
  );
};

export default FileUploadProgress;
